import { useCallback, useEffect, useMemo, useRef } from "react";
import tippy, { Instance as Tippy } from 'tippy.js';
import { cssRule } from 'typestyle';
import { COLOR_WHITE, FONT_WEIGHT_REGULAR, MARGINS_PX } from "../constants";
import { mixinFont } from "../style.mixin";

const duration = 300;

const useTooltip = <T = HTMLDivElement>(
    initialContent?: string | null,
    hoverTrigger: boolean = true
) => {
    const contentRef = useRef(initialContent || '');
    const hoverTriggerRef = useRef(hoverTrigger);
    const htmlElementRef = useRef<T>(null);
    const tippyMutableRef = useRef<Tippy>();

    const setContent = useCallback((content: string) => {
        if (tippyMutableRef.current && (contentRef.current !== content)) {
            contentRef.current = content;
            tippyMutableRef.current.setContent(content);
        }
    }, []);

    const show = useCallback((tooltipContent?: string) => {
        if (!tippyMutableRef.current) return;

        if (!tippyMutableRef.current.state.isVisible) {
            tooltipContent && setContent(tooltipContent);
            tippyMutableRef.current.show();
        }
        else if (tooltipContent && (contentRef.current !== tooltipContent)) {
            setContent(tooltipContent);
        }
    }, [setContent]);

    const hide = useCallback(() => {
        if (tippyMutableRef.current && tippyMutableRef.current.state.isVisible) {
            tippyMutableRef.current.hide();
        }
    }, []);

    useEffect(() => {
        const content = contentRef.current;
        const hoverTrigger = hoverTriggerRef.current;

        if (htmlElementRef.current) {
            tippyMutableRef.current = tippy(htmlElementRef.current as unknown as HTMLElement, {
                trigger: hoverTrigger ? 'mouseenter focus' : 'manual',
                hideOnClick: false,
                placement: 'auto',
                delay: 0,
                animation: 'custom-fade',
                duration,
                content,
                onHidden(instance) {
                    setContent(content);
                }
            });

            return () => {
                tippyMutableRef.current?.unmount();
                tippyMutableRef.current?.destroy();
                tippyMutableRef.current = undefined;
            };
        }
    }, [setContent]);

    return useMemo(() => ({
        ref: htmlElementRef,
        show,
        hide
    }), [hide, show]);
};

export default useTooltip;

cssRule('[data-tippy-root]', {
    maxWidth: 'calc(100% - 10px)',
});

cssRule('.tippy-box', {
    background: 'rgba(0, 0, 0, .8)',
    padding: `${MARGINS_PX / 2}px ${MARGINS_PX}px`,
    borderRadius: '4px',
    ...mixinFont(13, FONT_WEIGHT_REGULAR, 15, COLOR_WHITE)
});

cssRule('.tippy-box > .tippy-arrow', {
    width: 0,
    height: 0,
    position: 'absolute',
    opacity: .8
});

cssRule('.tippy-box[data-placement="top"] > .tippy-arrow', {
    borderTop: '8px solid black',
    borderLeft: '8px solid transparent',
    borderRight: '8px solid transparent',
    top: '100%'
});

cssRule('.tippy-box[data-placement="bottom"] > .tippy-arrow', {
    borderBottom: '8px solid black',
    borderLeft: '8px solid transparent',
    borderRight: '8px solid transparent',
    bottom: '100%'
});

cssRule('.tippy-box[data-placement="right"] > .tippy-arrow', {
    borderTop: '8px solid transparent',
    borderBottom: '8px solid transparent',
    borderRight: '8px solid black',
    right: '100%'
});

cssRule('.tippy-box[data-placement="left"] > .tippy-arrow', {
    borderTop: '8px solid transparent',
    borderBottom: '8px solid transparent',
    borderLeft: '8px solid black',
    left: '100%'
});

cssRule('.tippy-box[data-animation="custom-fade"][data-state="hidden"]', {
    opacity: 0
});
